// The messenger (legacy or unified) defines window.Intercom when its component
// mounts. The unified one mounts only after the JWT fetch, so anything that
// subscribes to messenger events (the AI chat bubble moving aside, the header
// resetting its support state) must wait for it rather than check once.
export const INTERCOM_READY_EVENT = 'confirm:intercom-ready';

type IntercomWindow = Window & { Intercom?: (...args: unknown[]) => void };

// Sticky: a child class component mounts before its parent's effects run, so
// a subscriber may ask after the event already fired.
let mounted = false;

export const isIntercomReady = (): boolean =>
  mounted ||
  (typeof window !== 'undefined' && !!(window as IntercomWindow).Intercom);

export const notifyIntercomReady = (): void => {
  mounted = true;
  window.dispatchEvent(new Event(INTERCOM_READY_EVENT));
};

export const notifyIntercomUnmounted = (): void => {
  mounted = false;
};

// Runs callback now if the messenger is already on the page, otherwise once
// it mounts. Returns the unsubscribe function for effect cleanup.
export const whenIntercomReady = (callback: () => void): (() => void) => {
  if (isIntercomReady()) {
    callback();
    return () => undefined;
  }

  const handler = () => callback();
  window.addEventListener(INTERCOM_READY_EVENT, handler, { once: true });
  return () => window.removeEventListener(INTERCOM_READY_EVENT, handler);
};
